import { AccessibleDivButton, LiveFeedback, SortMenu } from '@/components';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { Tag } from '@/design-system';
import { css } from '@emotion/css';
import colors from '@/theme/colors';
import { useEffect, useRef, useState } from 'react';
import { useLiveFeedback } from '@/hooks';
import { useSearchParams } from 'react-router-dom';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';

export const EmbeddedJarFilters = () => {
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [liveMessage, setLiveMessage] = useLiveFeedback({ delay: 2500 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedSortValue, setSelectedSortValue] = useState<string>(
    searchParams.get('sortBy') || 'flyerId'
  );
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const newSearchParams = new URLSearchParams(searchParams);

  const { scrollY } = useScroll();
  const [hidden, setHidden] = useState(false);
  const lastToggleYRef = useRef<number>(0);
  const distanceInDirectionRef = useRef<number>(0);
  const lastDirectionRef = useRef<'down' | 'up'>('down');
  const lastToggleAtRef = useRef<number>(0);

  // Asymmetric thresholds to reduce flicker
  const HIDE_DISTANCE_PX = 64; // need more distance to hide
  const SHOW_DISTANCE_PX = 36; // need less distance to show
  const MIN_TOGGLE_INTERVAL_MS = 500; // debounce layout/animation-induced jitter
  const EPSILON = 0.5; // ignore tiny changes

  useEffect(() => {
    const initial = scrollY.get();
    lastToggleYRef.current = initial;
    if (initial <= 0) {
      setHidden(false);
    }
  }, [scrollY]);

  useMotionValueEvent(scrollY, 'change', current => {
    const prev = scrollY.getPrevious() ?? current;
    const diff = current - prev;
    const now = Date.now();

    // Always show at top
    if (current <= 0) {
      if (hidden) {
        setHidden(false);
        lastToggleAtRef.current = now;
        lastToggleYRef.current = current;
      }
      distanceInDirectionRef.current = 0;
      lastDirectionRef.current = 'down';
      return;
    }

    if (Math.abs(diff) < EPSILON) return;

    const direction: 'down' | 'up' = diff > 0 ? 'down' : 'up';

    // Reset accumulation on direction change
    if (direction !== lastDirectionRef.current) {
      distanceInDirectionRef.current = 0;
      lastDirectionRef.current = direction;
    }

    // Accumulate absolute distance while moving in a direction
    distanceInDirectionRef.current += Math.abs(diff);

    // Debounce toggles
    if (now - lastToggleAtRef.current < MIN_TOGGLE_INTERVAL_MS) return;

    if (!hidden && direction === 'down') {
      if (distanceInDirectionRef.current >= HIDE_DISTANCE_PX) {
        setHidden(true);
        lastToggleAtRef.current = now;
        lastToggleYRef.current = current;
        distanceInDirectionRef.current = 0;
      }
    } else if (hidden && direction === 'up') {
      if (distanceInDirectionRef.current >= SHOW_DISTANCE_PX) {
        setHidden(false);
        lastToggleAtRef.current = now;
        lastToggleYRef.current = current;
        distanceInDirectionRef.current = 0;
      }
    }
  });

  const sortOptions = [
    { value: 'flyerId', label: 'Recently posted' },
    { value: 'title', label: 'Title (A-Z)' },
  ];

  const filters = ['all', 'school', 'community', 'free'];

  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter) {
      setSelectedFilter(filter);
    }
  }, [searchParams]);

  const handleFilterClick = (filter: string) => {
    setSelectedFilter(filter);
    newSearchParams.set('filter', filter);
    setSearchParams(newSearchParams, { replace: true });
    setLiveMessage(`${filter} filter selected.`);
  };

  const handleSortChange = (value: string) => {
    setSelectedSortValue(value);

    newSearchParams.set('sortBy', value);
    setSearchParams(newSearchParams, { replace: true });

    const selectedOptionLabel = sortOptions.find(option => option.value === value)?.label || value;
    setLiveMessage(`Sort updated to ${selectedOptionLabel}.`);
    handleCloseSortMenu();
  };

  const handleCloseSortMenu = () => {
    setIsSortMenuOpen(false);
    anchorRef.current?.focus();
  };

  return (
    <>
      <LiveFeedback message={liveMessage} />
      <AnimatePresence initial={false} mode="wait">
        {!hidden && (
          <motion.div
            className={cn.filtersWrapper}
            key="filters-wrapper"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: { duration: 0.3, ease: 'easeInOut' },
              opacity: { duration: 0.2 },
            }}
            style={{ overflow: 'hidden' }}
          >
            <div className={cn.container}>
              <fieldset className={cn.filtersContainer}>
                <legend className={cn.visuallyHidden}>Filter by type</legend>
                {filters.map(filter => {
                  const isActive = filter === selectedFilter;
                  return (
                    <label key={filter} className={cn.radioLabel}>
                      <input
                        type="radio"
                        name="embedded-jar-category-filter"
                        value={filter}
                        checked={isActive}
                        onClick={() => {
                          if (isActive) {
                            handleFilterClick(filter);
                          }
                        }}
                        onChange={() => {
                          if (!isActive) {
                            handleFilterClick(filter);
                          }
                        }}
                        className={cn.radioInput}
                        aria-label={`Select ${filter} filter`}
                        aria-pressed={isActive}
                      />
                      <Tag variant={isActive ? 'active' : 'outline'} className={cn.filterButton}>
                        {filter}
                      </Tag>
                    </label>
                  );
                })}
              </fieldset>
              <div>
                <AccessibleDivButton
                  onClick={() => setIsSortMenuOpen(!isSortMenuOpen)}
                  ref={anchorRef}
                >
                  <Tag variant="outline" rightIcon={<SwapVertIcon sx={{ fontSize: '15px' }} />}>
                    Sort
                  </Tag>
                </AccessibleDivButton>
              </div>
              <SortMenu
                open={isSortMenuOpen}
                onClose={handleCloseSortMenu}
                onChange={handleSortChange}
                selectedOption={selectedSortValue}
                anchorRef={anchorRef}
                options={sortOptions}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const cn = {
  filtersWrapper: css`
    background: ${colors.prussian100};
    width: 100%;
    z-index: 9999;
    overflow: hidden;
    padding: 2px;
  `,
  container: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    background-color: ${colors.prussian100};
    border-bottom: ${`solid 1px ${colors.stone}`};
    flex-wrap: wrap;
    gap: 8px;
  `,
  filtersContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    border: none;
    padding: 0;
    margin: 0;
  `,
  radioLabel: css`
    cursor: pointer;
    display: inline-flex;
    outline: none;
  `,
  radioInput: css`
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;

    &:focus-visible + div {
      box-shadow: 0 0 0 2px ${colors.focusOutlineColor};
    }
  `,
  visuallyHidden: css`
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `,
  filterButton: css`
    text-transform: capitalize;
  `,
};
